import { useEffect, useState } from 'react'
import { Alert, Form, Input, InputNumber, Modal } from 'antd'
import type { ApiKeyRecord } from '../../../shared/services/api'
import { formatMoney } from '../../../shared/utils/money'

type TopUpAccount = Pick<ApiKeyRecord, 'id' | 'name' | 'balance'>

export function TopUpAccountModal({
  account,
  onCancel,
  onSubmit,
}: {
  account: TopUpAccount | null
  onCancel: () => void
  onSubmit: (accountId: number, amount: number, comment: string) => Promise<void>
}) {
  const [form] = Form.useForm<{ amount: number; comment: string }>()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (account) form.setFieldsValue({ amount: undefined, comment: '' })
  }, [account, form])

  return (
    <Modal
      open={Boolean(account)}
      title={`Пополнение счёта${account ? ` — ${account.name}` : ''}`}
      okText="Пополнить"
      cancelText="Отмена"
      confirmLoading={submitting}
      destroyOnHidden
      onCancel={onCancel}
      onOk={() => form.submit()}
    >
      {account && (
        <Form
          form={form}
          layout="vertical"
          onFinish={async ({ amount, comment }) => {
            setSubmitting(true)
            try {
              await onSubmit(account.id, amount, comment)
              form.resetFields()
            } finally {
              setSubmitting(false)
            }
          }}
        >
          <Alert
            className="account-top-up-balance"
            type="info"
            showIcon
            message={`Доступно сейчас: ${formatMoney(account.balance.available, account.balance.currency)}`}
          />
          <Form.Item
            name="amount"
            label="Сумма пополнения"
            rules={[{ required: true, message: 'Укажите сумму' }, { type: 'number', min: 0.000001, message: 'Сумма должна быть больше нуля' }]}
          >
            <InputNumber
              autoFocus
              min={0.000001}
              precision={6}
              addonAfter={account.balance.currency}
              placeholder="0,00"
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="comment" label="Комментарий">
            <Input.TextArea rows={3} maxLength={1000} placeholder="Например: пополнение за август" showCount />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
